import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Settings,
  Shield,
  Key,
  Cpu,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Save,
  TestTube,
  Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAIConfig() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  
  const [config, setConfig] = useState({
    google_api_key_set: false,
    google_api_key_masked: '',
    openai_api_key_set: false,
    openai_api_key_masked: '',
    emergent_api_key_set: false,
    emergent_api_key_masked: '',
    primary_provider: 'google',
    primary_model: 'gemma-3-27b-it',
    fallback_provider_1: 'emergent',
    fallback_model_1: 'gpt-4o',
    fallback_provider_2: 'openai',
    fallback_model_2: 'gpt-4o',
    google_models: [],
    openai_models: [],
    emergent_models: []
  });
  
  const [newKeys, setNewKeys] = useState({
    google_api_key: '',
    openai_api_key: '',
    emergent_api_key: ''
  });
  
  const [showKeys, setShowKeys] = useState({
    google: false,
    openai: false,
    emergent: false
  });

  const loadConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/ai-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load AI configuration');
      }
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updates = {
        primary_provider: config.primary_provider,
        primary_model: config.primary_model,
        fallback_provider_1: config.fallback_provider_1,
        fallback_model_1: config.fallback_model_1,
        fallback_provider_2: config.fallback_provider_2,
        fallback_model_2: config.fallback_model_2
      };
      
      // Only include API keys if they were changed
      if (newKeys.google_api_key) {
        updates.google_api_key = newKeys.google_api_key;
      }
      if (newKeys.openai_api_key) {
        updates.openai_api_key = newKeys.openai_api_key;
      }
      if (newKeys.emergent_api_key) {
        updates.emergent_api_key = newKeys.emergent_api_key;
      }
      
      await axios.put(`${API}/admin/ai-config`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('AI configuration saved successfully');
      setNewKeys({ google_api_key: '', openai_api_key: '', emergent_api_key: '' });
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestKey = async (provider) => {
    const keyToTest = newKeys[`${provider}_api_key`] || 
      (provider === 'google' && config.google_api_key_set ? 'existing' : null);
    
    if (!keyToTest && provider === 'google' && !config.google_api_key_set) {
      toast.error('Please enter an API key to test');
      return;
    }
    
    setTesting(provider);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/ai-config/test`, {
        provider,
        api_key: newKeys[`${provider}_api_key`] || 'use_existing'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(null);
    }
  };

  const getModelsForProvider = (provider) => {
    switch (provider) {
      case 'google':
        return [
          { name: 'gemma-3-27b-it', display_name: 'Gemma 3 27B IT' },
          { name: 'gemini-1.5-flash', display_name: 'Gemini 1.5 Flash' },
          { name: 'gemini-1.5-pro', display_name: 'Gemini 1.5 Pro' },
          { name: 'gemini-2.0-flash', display_name: 'Gemini 2.0 Flash' },
          { name: 'gemini-2.5-flash', display_name: 'Gemini 2.5 Flash' }
        ];
      case 'openai':
        return config.openai_models?.length > 0 ? config.openai_models : [
          { name: 'gpt-4o', display_name: 'GPT-4o' },
          { name: 'gpt-4o-mini', display_name: 'GPT-4o Mini' },
          { name: 'gpt-4-turbo', display_name: 'GPT-4 Turbo' }
        ];
      case 'emergent':
        return config.emergent_models?.length > 0 ? config.emergent_models : [
          { name: 'gpt-4o', display_name: 'GPT-4o (Emergent)' },
          { name: 'gpt-4o-mini', display_name: 'GPT-4o Mini (Emergent)' }
        ];
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#0B2B5C]" />
          <span className="ml-2 text-muted-foreground">Loading configuration...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-ai-config">
        {/* Header */}
        <div>
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="mb-2 -ml-2 text-[#0B2B5C] hover:text-[#0B2B5C] hover:bg-[#0B2B5C]/5"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Admin Panel
          </Button>
          <h1 className="text-3xl font-manrope font-bold text-[#0B2B5C]">
            <Cpu className="inline-block mr-3 text-[#FFD700]" size={32} />
            AI Configuration
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure AI providers, API keys, and model priority for invoice extraction
          </p>
        </div>

        {/* API Keys Section */}
        <Card className="border-[#0B2B5C]/10 shadow-md">
          <CardHeader>
            <CardTitle className="text-xl font-manrope text-[#0B2B5C] flex items-center gap-2">
              <Key size={20} />
              API Keys
            </CardTitle>
            <CardDescription>
              Configure API keys for each AI provider. Keys are stored securely and never exposed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Google API Key */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium flex items-center gap-2">
                  Google AI (Gemini)
                  {config.google_api_key_set ? (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <CheckCircle size={12} className="mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-600 border-red-600">
                      <XCircle size={12} className="mr-1" /> Not Set
                    </Badge>
                  )}
                </Label>
                {config.google_api_key_masked && (
                  <span className="text-sm text-muted-foreground font-mono">
                    Current: {config.google_api_key_masked}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    type={showKeys.google ? 'text' : 'password'}
                    placeholder="Enter new Google API key..."
                    value={newKeys.google_api_key}
                    onChange={(e) => setNewKeys({ ...newKeys, google_api_key: e.target.value })}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys({ ...showKeys, google: !showKeys.google })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showKeys.google ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => handleTestKey('google')}
                  disabled={testing === 'google' || (!newKeys.google_api_key && !config.google_api_key_set)}
                >
                  {testing === 'google' ? <Loader2 size={16} className="animate-spin" /> : <TestTube size={16} />}
                  <span className="ml-2">Test</span>
                </Button>
              </div>
            </div>

            <Separator />

            {/* OpenAI API Key */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium flex items-center gap-2">
                  OpenAI
                  {config.openai_api_key_set ? (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <CheckCircle size={12} className="mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-600 border-red-600">
                      <XCircle size={12} className="mr-1" /> Not Set
                    </Badge>
                  )}
                </Label>
                {config.openai_api_key_masked && (
                  <span className="text-sm text-muted-foreground font-mono">
                    Current: {config.openai_api_key_masked}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    type={showKeys.openai ? 'text' : 'password'}
                    placeholder="Enter new OpenAI API key..."
                    value={newKeys.openai_api_key}
                    onChange={(e) => setNewKeys({ ...newKeys, openai_api_key: e.target.value })}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys({ ...showKeys, openai: !showKeys.openai })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showKeys.openai ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => handleTestKey('openai')}
                  disabled={testing === 'openai' || (!newKeys.openai_api_key && !config.openai_api_key_set)}
                >
                  {testing === 'openai' ? <Loader2 size={16} className="animate-spin" /> : <TestTube size={16} />}
                  <span className="ml-2">Test</span>
                </Button>
              </div>
            </div>

            <Separator />

            {/* Emergent API Key */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium flex items-center gap-2">
                  Emergent AI
                  {config.emergent_api_key_set ? (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <CheckCircle size={12} className="mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-600 border-red-600">
                      <XCircle size={12} className="mr-1" /> Not Set
                    </Badge>
                  )}
                </Label>
                {config.emergent_api_key_masked && (
                  <span className="text-sm text-muted-foreground font-mono">
                    Current: {config.emergent_api_key_masked}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    type={showKeys.emergent ? 'text' : 'password'}
                    placeholder="Enter new Emergent API key..."
                    value={newKeys.emergent_api_key}
                    onChange={(e) => setNewKeys({ ...newKeys, emergent_api_key: e.target.value })}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys({ ...showKeys, emergent: !showKeys.emergent })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showKeys.emergent ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => handleTestKey('emergent')}
                  disabled={testing === 'emergent' || (!newKeys.emergent_api_key && !config.emergent_api_key_set)}
                >
                  {testing === 'emergent' ? <Loader2 size={16} className="animate-spin" /> : <TestTube size={16} />}
                  <span className="ml-2">Test</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Model Priority Section */}
        <Card className="border-[#0B2B5C]/10 shadow-md">
          <CardHeader>
            <CardTitle className="text-xl font-manrope text-[#0B2B5C] flex items-center gap-2">
              <Settings size={20} />
              Model Priority
            </CardTitle>
            <CardDescription>
              Configure which AI provider and model to use first, and fallback options if it fails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Provider */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Provider</Label>
                <Select
                  value={config.primary_provider}
                  onValueChange={(v) => setConfig({ ...config, primary_provider: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google AI (Gemini)</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="emergent">Emergent AI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Primary Model</Label>
                <Select
                  value={config.primary_model}
                  onValueChange={(v) => setConfig({ ...config, primary_model: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getModelsForProvider(config.primary_provider).map((model) => (
                      <SelectItem key={model.name} value={model.name}>
                        {model.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Fallback 1 */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fallback Provider 1</Label>
                <Select
                  value={config.fallback_provider_1}
                  onValueChange={(v) => setConfig({ ...config, fallback_provider_1: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Skip)</SelectItem>
                    <SelectItem value="google">Google AI (Gemini)</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="emergent">Emergent AI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fallback Model 1</Label>
                <Select
                  value={config.fallback_model_1}
                  onValueChange={(v) => setConfig({ ...config, fallback_model_1: v })}
                  disabled={config.fallback_provider_1 === 'none'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getModelsForProvider(config.fallback_provider_1).map((model) => (
                      <SelectItem key={model.name} value={model.name}>
                        {model.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Fallback 2 */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fallback Provider 2</Label>
                <Select
                  value={config.fallback_provider_2}
                  onValueChange={(v) => setConfig({ ...config, fallback_provider_2: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Skip)</SelectItem>
                    <SelectItem value="google">Google AI (Gemini)</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="emergent">Emergent AI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fallback Model 2</Label>
                <Select
                  value={config.fallback_model_2}
                  onValueChange={(v) => setConfig({ ...config, fallback_model_2: v })}
                  disabled={config.fallback_provider_2 === 'none'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getModelsForProvider(config.fallback_provider_2).map((model) => (
                      <SelectItem key={model.name} value={model.name}>
                        {model.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0B2B5C] hover:bg-[#0B2B5C]/90 text-white px-8"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
